import { computed, extendObservable, makeObservable } from "mobx";
import MovimentoService from "../services/MovimentoService";
import { IAutocomplete } from "./Autocomplete";
import { Processo } from "./Processo";
import ProcessoStore from "@/store/ProcessoStore";

export class MovimentoBasicoDTO {
    codMovimento: string;
    codMovimentoCnj: string;
    complementos: ComplementoBasicoDTO[];
    descricaoMovimento: string;
    descricaoMovimentoCnj: string;
    origem: string;
    tipoMovimento?: string;
}

export class ComplementoBasicoDTO {
    codDominio: number;
    codMovimento: number;
    codigo: string;
    dominio: string;
    id: number;
    origem: string;
    valor: string;
}

export class MinutaMovimentos {
    numMinuta: number;
    movimentos: MovimentoGEDTO[];
}

export class MovimentoGEDTO {
    id: number;
    numMinuta: number;
    numOrdem: number;
    codMovimentoCnj: string;
    codMovimentoUnificacao: string;
    complementos: ComplementoGEDTO[];
    tipoMovimento?: string;
    complementosPossiveis?: ComplementosPossiveisDto[];
    complementosAutomaticosSetados?: boolean = false;

    descricaoMovimento?: string;
    descricaoMovimentoUnificacao?: string;
    origem?: string;
    mostrar?: boolean;

    // processo?: Processo;
    idProcesso?: number;

    constructor() {
        makeObservable(this, {
            isComplementosPreenchidos: computed
        });
    }

    get processo() : Processo{
        return ProcessoStore._processos.find(proc => proc.identificadorProcesso == this.idProcesso);
    }

    static mobxInicial = () => ({
        complementos: [],
        complementosPossiveis: []
    })

    static novo(dto: Partial<MovimentoGEDTO>, processo?: Processo) {
        if (!dto) {
            dto = {};
        }

        const mov = new MovimentoGEDTO();
        const mobxInicial = MovimentoGEDTO.mobxInicial();
        extendObservable(mov, mobxInicial);

        mov.idProcesso = processo?.identificadorProcesso;
        const merge = Object.assign(mov, dto);

        if (!merge.origem) {
            merge.origem = merge.origem || merge.codMovimentoCnj[0];
            // merge.codMovimentoCnj = parseInt(dto.codMovimentoCnj.toString().replace(/[A-Z]-/g, ''));
        }
        merge.complementos = JSON.parse(JSON.stringify(dto.complementos || []));
        merge.complementosPossiveis = JSON.parse(JSON.stringify(dto.complementosPossiveis || []));
        return merge;
    }

    static toGEDTO(dto: Partial<MovimentoBasicoDTO>, considerarCompPossivel?: boolean) {
        if (!dto) {
            dto = {};
        }

        const mov = new MovimentoGEDTO();
        const mobxInicial = MovimentoGEDTO.mobxInicial();
        extendObservable(mov, mobxInicial);

        mov.codMovimentoCnj = dto.codMovimento;
        mov.codMovimentoUnificacao = dto.codMovimentoCnj;
        mov.tipoMovimento = dto.tipoMovimento;
        if (considerarCompPossivel) {
            const stringsComplemento: string[] = [];
            dto.complementos?.forEach(c => {
                if (!stringsComplemento.includes(c.dominio)) {
                    stringsComplemento.push(c.dominio);
                }
            });

            stringsComplemento.forEach(dominio => {
                const complementoPossivel = new ComplementosPossiveisDto();
                // complementoPossivel.codComplementoCnj: string;
                complementoPossivel.stringComplemento = dominio;
                complementoPossivel.codElementoDominio = dto.complementos[0].codDominio;
                // complementoPossivel.tipoComplemento: TipoComplementoGEDTO;
                complementoPossivel.opcoes = dto.complementos.filter(d => d.dominio === dominio)
                    .map(c => ComplementosPossiveisDto.fromBasicoDTOtoComplementoPossivelOpcao(c));
                mov.complementosPossiveis.push(complementoPossivel);
            })

        } else {
            mov.complementos = dto.complementos?.map(c => ComplementoGEDTO.toGEDTO(c));
        }

        mov.descricaoMovimento = dto.descricaoMovimento;
        mov.descricaoMovimentoUnificacao = dto.descricaoMovimentoCnj;

        mov.origem = dto.origem;

        return mov;
    }

    get variaveis(): string[] {
        const complementosPossiveis = this.complementosPossiveis?.map(comp => comp.stringComplemento);
        return this.descricaoMovimento?.match(/#{[^{}]+}/gi)?.map(a => a.substring(2, a.length - 1)) || (complementosPossiveis.length && complementosPossiveis) || [];
    }

    get isComplementosPreenchidos(): boolean {
        return this.variaveis.every(variavel => {
            const valor = this.complementos?.find(comp => comp.stringComplemento?.toLocaleLowerCase().includes(variavel?.toLowerCase()))?.valorSetado;
            const existeExcludente = MovimentoService.existeComplementoExcludente(variavel, this);
            return existeExcludente ? (valor || MovimentoService.variavelExcludentePreenchida(variavel, this, this.processo)) : valor;
        });
    }

    toJSON() {
        const tojson = {
            ...this,
            tipoMovimento: undefined,
            complementosPossiveis: undefined,
            descricaoMovimento: undefined,
            origem: undefined,
            mostrar: undefined,
            processo: undefined
        }
        return tojson;
    }
}

export class ComplementoGEDTO {
    codElementoDominio: number; // cod do elemento de dominio salvo no banco do GE
    codComplementoCnj: number; // cod cnj salvo no banco do GE
    valorComplemento: string; // valor salvo no banco do GE

    stringComplemento?: string; // ou nome de dominio.. se a variavel é '#{nome da parte}' aqui ficaria so 'nome da parte'
    valor?: string; // valor do json que será enviado ao backend
    valorSetado?: string; // valor setado no autocomplete


    static mobxInicial = () => ({
        valorSetado: ''
    })

    static novo(dto: Partial<ComplementoGEDTO>) {
        if (!dto) {
            dto = {};
        }

        const comp = new ComplementoGEDTO();
        const mobxInicial = ComplementoGEDTO.mobxInicial();
        extendObservable(comp, mobxInicial);

        if (!dto.valorComplemento && dto.valorSetado) {
            dto.valorComplemento = dto.valorSetado;
        } else {
            dto.valorSetado = dto.valorComplemento;
        }


        return Object.assign(comp, dto);
    }

    static toGEDTO(dto: Partial<ComplementoBasicoDTO>): ComplementoGEDTO {
        if (!dto) {
            dto = {};
        }

        const comp = new ComplementoGEDTO();
        const mobxInicial = ComplementoGEDTO.mobxInicial();
        extendObservable(comp, mobxInicial);

        comp.codElementoDominio = parseInt(dto.codigo);
        comp.codComplementoCnj = dto.codDominio;

        comp.valorComplemento = dto.valor;

        comp.stringComplemento = dto.dominio;
        comp.valor = dto.valor;

        return comp;
    }

    static fromPossivelToGEDTO(dto: Partial<ComplementosPossiveisDto>): ComplementoGEDTO {
        if (!dto) {
            dto = {};
        }

        const comp = new ComplementoGEDTO();
        const mobxInicial = ComplementoGEDTO.mobxInicial();
        extendObservable(comp, mobxInicial);

        comp.codElementoDominio = dto.codElementoDominio;
        comp.codComplementoCnj = parseInt(dto.codComplementoCnj);

        comp.valorComplemento = '';

        comp.stringComplemento = dto.stringComplemento;
        comp.valor = '';

        return comp;
    }

    // noinspection JSUnusedGlobalSymbols
    toJSON() {
        return {
            ...this,
            stringComplemento: undefined,
            valorSetado: undefined
        }
    }
}

export class MovimentoComplementoDto {
    id: number;
    codigo: string;
    tipoComplemento: TipoComplementoGEDTO;
    tipoComplementoDinamico: string;
    descricao: string;
    nomeParametrizado: string;
    parExcludente: string;
    opcoes: OpcaoMovimentoComplementoDto[];
}

export class OpcaoMovimentoComplementoDto {
    id: number;
    codigo: string;
    valor: string;
}

export class ComplementosPossiveisDto {
    codComplementoCnj: string;
    stringComplemento: string;
    codElementoDominio: number;
    tipoComplemento: TipoComplementoGEDTO;
    opcoes: IAutocomplete<ComplementoGEDTO>[]; // todos os valores possíveis pra esse complemento ... se for 'nome da parte' aqui vai ter a lista com os nomes das partes

    static mobxInicial = () => ({
    })

    static fromBasicoDTOtoComplementoPossivelOpcao(dto: Partial<ComplementoBasicoDTO>): IAutocomplete<ComplementoGEDTO> {
        if (!dto) {
            dto = {};
        }

        const compPossivel = new ComplementoGEDTO();

        const mobxInicial = ComplementosPossiveisDto.mobxInicial();
        extendObservable(compPossivel, mobxInicial);

        const comp = new ComplementoGEDTO();
        comp.codElementoDominio = dto.id;
        comp.codComplementoCnj = parseInt(dto.codigo);
        comp.stringComplemento = dto.dominio;
        // comp.valorComplemento = dto.valor;
        comp.valor = dto.valor;

        return {
            valor: comp,
            texto: dto.valor,
            parametrosBusca: [dto.valor]
        };

    }

    static ToComplementoPossivelDto(dto: Partial<MovimentoComplementoDto>): ComplementosPossiveisDto {
        if (!dto) {
            dto = {};
        }

        const compPossivel = new ComplementosPossiveisDto();


        const mobxInicial = ComplementosPossiveisDto.mobxInicial();
        extendObservable(compPossivel, mobxInicial);

        compPossivel.codElementoDominio = dto.id;
        compPossivel.codComplementoCnj = dto.codigo;
        compPossivel.tipoComplemento = dto.tipoComplemento;

        compPossivel.stringComplemento = dto.descricao.toLocaleLowerCase();

        compPossivel.opcoes = dto.opcoes.map(opcao => {

            const comp = new ComplementoGEDTO();
            comp.codElementoDominio = dto.id;
            comp.codComplementoCnj = parseInt(dto.codigo);
            comp.stringComplemento = dto.descricao;
            comp.valorComplemento = JSON.stringify(opcao);
            comp.valor = opcao.valor;

            return {
                valor: comp,
                texto: opcao.valor,
                parametrosBusca: [opcao.valor]
            };
        });

        return compPossivel;
    }
}

export type TipoComplementoGEDTO = 'LIVRE' | 'DOMINIO' | 'DINAMICO' | 'PROCESSO';

export const TipoComplemento_LIVRE = 'LIVRE';
export const TipoComplemento_DOMINIO = 'DOMINIO';
export const TipoComplemento_DINAMICO = 'DINAMICO';
export const TipoComplemento_PROCESSO = 'PROCESSO';